import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CreateAdminCompanyClosureArchiveAPI,
  GetAdminCompanyClosureArchiveAPI,
  GetAdminClosureArchiveDownloadUrlAPI,
} from '../../../../api/AdminAPI';

const POLL_INTERVAL_MS = 2000;

const isRunning = (closure) => {
  const status = closure?.archive_job?.status;
  return !closure?.archive_ready && (status === 'pending' || status === 'processing');
};

/**
 * useCompanyClosureArchive
 *
 * Estado do encerramento de uma empresa na tela do Admin Geral: parte do
 * `closure` devolvido por get.php, acompanha a geração do arquivo de segurança
 * (consulta a cada 2 s enquanto o job está pendente ou em andamento) e abre o
 * link assinado de cada parte.
 *
 * @param {number|string} companyId
 * @param {Object|null} initialClosure
 * @param {Function} [onArchiveReady] - chamado quando o arquivo fica pronto
 */
function useCompanyClosureArchive(companyId, initialClosure, onArchiveReady) {
  const [closure, setClosure] = useState(initialClosure || null);
  const [creating, setCreating] = useState(false);
  const [downloadingPart, setDownloadingPart] = useState(null);
  const [error, setError] = useState('');
  const readyCallback = useRef(onArchiveReady);
  readyCallback.current = onArchiveReady;

  useEffect(() => {
    setClosure(initialClosure || null);
  }, [initialClosure]);

  const running = isRunning(closure);

  useEffect(() => {
    if (!running) return undefined;
    const timer = setInterval(() => {
      GetAdminCompanyClosureArchiveAPI(companyId, (response) => {
        if (!response || response.success === false) return;
        setClosure(response.closure || null);
        if (response.closure?.archive_ready && readyCallback.current) {
          readyCallback.current(response.closure);
        }
      });
    }, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [companyId, running]);

  const createArchive = useCallback(() => {
    if (creating) return;
    setCreating(true);
    setError('');
    CreateAdminCompanyClosureArchiveAPI(companyId, (response) => {
      setCreating(false);
      if (!response || response.success === false) {
        setError(response?.error || 'Não foi possível gerar o arquivo de segurança. Tente novamente.');
        return;
      }
      setClosure(response.closure || null);
    });
  }, [companyId, creating]);

  const downloadPart = useCallback((index) => {
    setDownloadingPart(index);
    setError('');
    GetAdminClosureArchiveDownloadUrlAPI(companyId, index, (response) => {
      setDownloadingPart(null);
      if (!response || response.success === false || !response.url) {
        setError(response?.error || 'Não foi possível gerar o link de download. Tente novamente.');
        return;
      }
      window.location.assign(response.url);
    });
  }, [companyId]);

  return { closure, running, creating, downloadingPart, error, createArchive, downloadPart };
}

export default useCompanyClosureArchive;
